import { RouteEnum } from "../../../common/enums/RouteEnum";
import {
    BasicQualificationInterface,
    QualificationInterface,
} from "../../../common/interfaces/QualificationInterface";
import historyImage from "../../../common/images/history.svg";
import infiniteImage from "../../../common/images/infinite.svg";
import checkImage from "../../../common/images/check-green.svg";
import schoolImage from "../../../common/images/school.svg";
import importantImage from "../../../common/images/important-orange.svg";
import addCircleImage from "../../../common/images/add-circle.svg";
import peopleGroupImage from "../../../common/images/peopleGroup.svg";
import {
    setIsAtQualification,
    setIsCreateCertificateModalOpen,
    setIsEstablishmentCertificate,
    setSelectedQualification,
} from "../../../modals/CreateCertificateModal/CreateCertificateModalSlice";
import { Dispatch } from "redux";
import { NavigateFunction } from "react-router";
import { ValidityPeriodIconEnum } from "../../../common/enums/ValidityPeriodIconEnum";
import { getValidityPeriodDisplay } from "../../../common/utils/getValidityPeriodDisplay";
import { PrimaryTableRow } from "@hatch-digital-nl/abs-storybook/legacy";
import { isQualificationAchieved } from "../../../common/utils/isQualificationAchieved";
import {
    getQualificationTypeLabel,
    QualificationTypeEnum,
} from "../../../common/enums/QualificationTypeEnum";
import { EstablishmentStatisticsInterface } from "../../../common/interfaces/EstablishmentStatisticsInterface";

interface Args {
    qualificationsData: QualificationInterface[];
    navigate: NavigateFunction;
    dispatch: Dispatch;
    isEstablishmentManager: boolean;
    establishmentAchievedQualifications: BasicQualificationInterface[];
    employeeAchievedQualifications: BasicQualificationInterface[];
    establishmentStatisticsData?: EstablishmentStatisticsInterface;
}

const formatAchievedEmployeeLine = (
    name: string,
    methodLabel: string,
): string => `${name} · ${methodLabel}`;

export const mapQualificationDataToRow = (args: Args): PrimaryTableRow[][] => {
    const {
        qualificationsData,
        dispatch,
        isEstablishmentManager,
        navigate,
        establishmentAchievedQualifications,
        employeeAchievedQualifications,
        establishmentStatisticsData,
    } = args;

    return qualificationsData?.map((qualification: QualificationInterface) => {
        const isEstablishmentQualification =
            qualification.type === QualificationTypeEnum.ESTABLISHMENT;
        const validityPeriodName = getValidityPeriodDisplay({
            validityPeriod: qualification.validity_period,
            validityType: qualification.validity_type,
            fixedValidUntil: qualification.fixed_valid_until,
        });

        const isAchieved = isQualificationAchieved({
            qualification,
            establishmentAchievedQualifications,
            employeeAchievedQualifications,
            isEstablishmentManager,
        });

        const holders = qualification.achieved_employees ?? [];

        const holderLines = holders
            .filter(
                (holder) =>
                    Boolean(holder.name) &&
                    Boolean(holder.achievement_method?.label),
            )
            .map((holder) =>
                formatAchievedEmployeeLine(
                    holder.name as string,
                    holder.achievement_method?.label as string,
                ),
            );

        const qualifiedEmployeesCount =
            establishmentStatisticsData?.qualifications?.find(
                (q) => q.id === qualification.id,
            )?.qualified_employees_count;

        return [
            {
                value: "",
                isFirstIcon: true,
                iconUrl: isAchieved ? checkImage : importantImage,
            },
            { value: qualification.name },
            ...(isEstablishmentManager
                ? [
                      {
                          value: getQualificationTypeLabel(qualification.type),
                      },
                  ]
                : []),
            {
                value: qualification.function_group?.length
                    ? qualification.function_group.map(
                          (functionGroup) => functionGroup.name,
                      )
                    : "",
            },
            {
                value: qualification.qualification_standards?.length
                    ? qualification.qualification_standards.map(
                          (qualificationStandard) => qualificationStandard.tag,
                      )
                    : "",
            },
            {
                value: validityPeriodName.text,
                iconUrl:
                    validityPeriodName.icon === ValidityPeriodIconEnum.INFINITE
                        ? infiniteImage
                        : historyImage,
            },
            ...(isEstablishmentManager
                ? [
                      !isEstablishmentQualification
                          ? {
                                value:
                                    holders.length +
                                    "/" +
                                    qualifiedEmployeesCount,
                                iconUrl: peopleGroupImage,
                                ...(holderLines.length > 0
                                    ? {
                                          tooltip: holderLines.join("\n"),
                                      }
                                    : {}),
                            }
                          : {
                                value: "-",
                            },
                  ]
                : []),
            {
                value: "",
                actionData: [
                    {
                        iconUrl: schoolImage,
                        onClick: () =>
                            navigate(
                                RouteEnum.QUALIFICATIONS +
                                    "/" +
                                    qualification.id,
                            ),
                        title: "Bekijken",
                    },
                    {
                        iconUrl: addCircleImage,
                        onClick: () => {
                            dispatch(setIsCreateCertificateModalOpen(true));
                            dispatch(setSelectedQualification(qualification));

                            if (
                                qualification.type ===
                                QualificationTypeEnum.ESTABLISHMENT
                            ) {
                                dispatch(setIsEstablishmentCertificate(true));
                            }

                            dispatch(setIsAtQualification(true));
                        },
                        title: "Certificaat toevoegen",
                    },
                ],
            },
        ];
    });
};
